import { Badge, Button, Group, Modal, Stack } from "@mantine/core";
import { IMinUserDepricated } from "@mctypes/global.types";
import { IAccessPopulated } from "@schemas/accessSchema";
import { IconArrowRight, IconEdit } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { EditSaveAbort, UserList } from "..";
import { AccessGroupsEnum, AccessLevelsEnum } from "../../mc-shared/enums";
import McDropdownNew from "../McDropdown/MCDropdownNew";

const ACCESSLEVELS: IAccessPopulatedLevelLabelAndValue[] = [
  { label: "Prosjekt", value: AccessLevelsEnum.PROJECT },
  { label: "Grupper", value: AccessLevelsEnum.GROUPS },
  { label: "Brukere", value: AccessLevelsEnum.USERS },
];
const ACCESSGROUPS: IAccessPopulatedGroupLabelAndValue[] = [
  { label: "Interne", value: AccessGroupsEnum.INTERNAL },
  { label: "Kunde", value: AccessGroupsEnum.CLIENT },
  { label: "Partner", value: AccessGroupsEnum.PARTNER },
];
interface IAccessPopulatedLevelLabelAndValue {
  label: string;
  value: AccessLevelsEnum;
}

interface IAccessPopulatedGroupLabelAndValue {
  label: string;
  value: AccessGroupsEnum;
}

type IAccessUserDropdownOption = IMinUserDepricated & {
  value: string;
  label: string;
  isActive: boolean;
};
export const AccessComp: React.FC<{
  access: IAccessPopulated;
  updateAccess: (newAccessLevel: IAccessPopulated) => void;
  allUsers: IMinUserDepricated[];
  isEditable?: boolean;
  editMode?: boolean;
  fontSize?: string;
  editCb?: (bool: boolean) => void;
  variant?: "sm" | "md";
}> = ({
  access,
  updateAccess,
  allUsers,
  isEditable = true,
  editMode = false,
  editCb,
  variant = "md",
  fontSize = "",
}) => {
  const [modifiedAccess, setModifiedAccess] = useState<IAccessPopulated>({
    level: AccessLevelsEnum.PROJECT,
    groups: [],
    users: [],
  });

  useEffect(() => {
    setModifiedAccess({ level: access.level, groups: access?.groups || [], users: access?.users || [] });
  }, [access]);

  const _save = async () => {
    if (modifiedAccess !== access) {
      await updateAccess(modifiedAccess);
      editCb(false);
    }
  };

  const activeAccessGroups = ACCESSGROUPS.map((item) => ({
    ...item,
    isActive: modifiedAccess?.groups.includes(item.value),
  }));

  const activeAccessUsers: IAccessUserDropdownOption[] = allUsers.map((user) => ({
    ...user,
    value: user._id,
    label: user.name,
    isActive: modifiedAccess?.users?.findIndex((i) => i._id === user._id) !== -1,
  }));

  const handleGroupDropdownChange = (selectedOptions: IAccessPopulatedGroupLabelAndValue[] = []) => {
    setModifiedAccess({ ...modifiedAccess, groups: selectedOptions.map((option) => option.value) });
  };

  const handleUserDropdownChange = (selectedOptions: IAccessUserDropdownOption[] = []) => {
    const nextUsers = selectedOptions.map((option) => ({ _id: option.value, name: option.label }));
    setModifiedAccess({ ...modifiedAccess, users: nextUsers });
  };

  const saveIsDisabled =
    (modifiedAccess.level === AccessLevelsEnum.USERS && modifiedAccess.users.length === 0) ||
    (modifiedAccess.level === AccessLevelsEnum.GROUPS && modifiedAccess.groups.length === 0) ||
    (modifiedAccess.level === AccessLevelsEnum.GROUPS && modifiedAccess.groups.length === 3);

  return (
    <>
      <div style={{ maxWidth: "24em" }}>
        <Stack gap="sm">
          {editMode === false && <AccessView access={access} fontSize={fontSize} />}
          {editMode === true && (
            <>
              <Group gap={"sm"}>
                <div>
                  <h5>Nivå</h5>
                  <McDropdownNew
                    currentValue={ACCESSLEVELS.find((b) => b.value === modifiedAccess.level)}
                    options={ACCESSLEVELS}
                    displayKey="label"
                    onChange={(oldValue, newValue) => setModifiedAccess({ ...modifiedAccess, level: newValue.value })}
                  />
                </div>
                {modifiedAccess.level === AccessLevelsEnum.GROUPS && (
                  <div>
                    <h5>Grupper</h5>
                    <McDropdownNew
                      currentValue={null as any}
                      onChange={() => undefined}
                      color="mc-blue"
                      variant="secondary-filled"
                      size="sm"
                      multiSelect
                      options={activeAccessGroups}
                      displayKey="label"
                      selectedDisplayKey="value"
                      defaultTitle="Velg grupper"
                      selectedValues={modifiedAccess.groups}
                      onMultiSelectChange={(selected) =>
                        handleGroupDropdownChange(selected as IAccessPopulatedGroupLabelAndValue[])
                      }
                      showSelectionBadge
                      withSelectAll={false}
                    />
                  </div>
                )}

                {modifiedAccess.level === AccessLevelsEnum.USERS && (
                  <div>
                    <h5>Brukere</h5>
                    <McDropdownNew
                      currentValue={null as any}
                      onChange={() => undefined}
                      color="mc-blue"
                      variant="secondary-filled"
                      size="sm"
                      multiSelect
                      options={activeAccessUsers}
                      displayKey="label"
                      selectedDisplayKey="value"
                      defaultTitle="Brukere"
                      selectedValues={modifiedAccess.users.map((user) => user._id)}
                      onMultiSelectChange={(selected) => handleUserDropdownChange(selected as typeof activeAccessUsers)}
                      withFilter={activeAccessUsers?.length > 10}
                      showSelectionBadge
                      showSelectedSection
                      withSelectAll
                    />
                  </div>
                )}
              </Group>
            </>
          )}

          {editMode === true && (
            <Group wrap="nowrap" gap={"xs"}>
              <AccessView access={access} onlyAccessText={true} />
              <IconArrowRight size={20} />
              <AccessView access={modifiedAccess} onlyAccessText={true} />
            </Group>
          )}

          {modifiedAccess.level === AccessLevelsEnum.USERS && (
            <>
              <h4>Brukere</h4>
              <div className="pr-4" style={{ maxHeight: "300px", overflow: "auto" }}>
                <UserList
                  addedUsers={modifiedAccess.users
                    .filter((u) => allUsers.some((au) => au._id === u._id))
                    .map((user) => ({
                      _id: user._id,
                      name: user.name,
                      canBeRemoved: true,
                    }))}
                  condensed
                />
              </div>
            </>
          )}

          <EditSaveAbort
            disabled={saveIsDisabled}
            editable={isEditable}
            big
            saveCb={_save}
            editMode={editMode}
            editModeCb={editCb}
          />
        </Stack>
      </div>
    </>
  );
};

export const generateAccessText = (
  access: IAccessPopulated,
  variant: "sm" | "md" = "md",
  onlyAccessText: boolean = false,
) => {
  let accessText = "";

  const groupLabels = access?.groups?.map((value) => {
    const accessGroup = ACCESSGROUPS.find((group) => group.value === value);
    return accessGroup?.label.toLowerCase();
  });

  if (access.level === AccessLevelsEnum.PROJECT) {
    accessText = "Alle i prosjektet";
  } else if (access.level === AccessLevelsEnum.GROUPS) {
    const resultString = `${groupLabels
      ?.filter(Boolean)
      ?.sort((a, b) => a.localeCompare(b))
      .join(" og ")}`;
    accessText = resultString.charAt(0).toUpperCase() + resultString.slice(1);
  } else if (access.level === AccessLevelsEnum.USERS) {
    accessText = "Spesifikke brukere";
  }

  if (variant !== "sm" && !onlyAccessText) {
    accessText += " har tilgang";
  }

  return accessText;
};

export const AccessView: React.FC<{
  access: IAccessPopulated;
  variant?: "sm" | "md";
  fontSize?: string;
  onlyAccessText?: boolean;
}> = ({ access, variant = "md", fontSize, onlyAccessText = false }) => {
  const generatedText = generateAccessText(access, variant, onlyAccessText);
  // Inner content reused for both variants
  const inner = (
    <div
      className="d-flex align-items-center justify-content-between w-100"
      style={{ fontSize: fontSize ? fontSize : variant === "sm" ? "0.8rem" : "0.9em" }}
    >
      {access.level === AccessLevelsEnum.PROJECT && (
        <div>
          <i className="fa fa-unlock mr-2 text-success" aria-hidden="true"></i>
          {generatedText}
        </div>
      )}
      {access.level === AccessLevelsEnum.USERS && (
        <div>
          <i className="fa fa-lock mr-2 text-danger" aria-hidden="true"></i>
          {generatedText}
        </div>
      )}
      {access.level === AccessLevelsEnum.GROUPS && (
        <div>
          <i className="fa fa-users mr-2 text-warning" aria-hidden="true"></i>
          {generatedText}
        </div>
      )}
    </div>
  );

  // For small variant, omit Badge wrapper & styling entirely
  if (variant === "sm") return inner;

  return (
    <Badge
      color={
        access.level === AccessLevelsEnum.PROJECT
          ? "green"
          : access.level === AccessLevelsEnum.GROUPS
          ? "yellow"
          : "red"
      }
      // Preserve optional border only for non-small variant
      bd={access.level === AccessLevelsEnum.GROUPS ? "1px solid #ffe68b" : "0px"}
    >
      {inner}
    </Badge>
  );
};

interface AccessHandlerProps {
  isEditable: boolean;
  allUsers: IMinUserDepricated[];
  access: IAccessPopulated;
  updateAccess: (newAccessLevel: IAccessPopulated) => void;
}

interface AccessHandlerContainerProps extends AccessHandlerProps {
  editMode: boolean;
  buttonSize?: "sm" | "md";
}

const AccessHandlerModal: React.FC<AccessHandlerContainerProps> = ({
  access,
  updateAccess,
  allUsers,
  editMode,
  buttonSize = "sm",
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const generatedText = generateAccessText(access, "md", true);
  return (
    <div>
      {editMode === false ? (
        <div className="gray-600-text">
          <AccessView access={access} />
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => setOpenModal(true)}
          size={buttonSize}
          style={{ fontSize: "0.9em" }}
          leftSection={<IconEdit size={16} />}
        >
          {generatedText}
        </Button>
      )}
      <Modal
        opened={openModal}
        onClose={() => setOpenModal(false)}
        title="Endre tilgang"
        size="md"
        transitionProps={{ transition: "fade", duration: 50 }}
        overlayProps={{
          backgroundOpacity: 0,
        }}
      >
        <AccessComp
          access={access}
          updateAccess={updateAccess}
          allUsers={allUsers}
          editMode={true}
          editCb={setOpenModal}
          variant="md"
        />
      </Modal>
    </div>
  );
};
export default AccessHandlerModal;
